import { useEffect } from "react";
import { useSettings } from "@/stores/settingsStore";
import { useT } from "@/i18n";
import LobbyLayout from "@/components/LobbyLayout";
import { SCREEN } from "@/stores/gameStore";
import { toast } from "sonner";
import { Users, Copy, RefreshCw, UserPlus, Ban, Search, X } from "lucide-react";

const TABS = [
    { id: "add", key: "friends.add" },
    { id: "online", key: "friends.online" },
    { id: "offline", key: "friends.offline" },
    { id: "incoming", key: "friends.incoming" },
    { id: "sent", key: "friends.sent" },
    { id: "blocked", key: "friends.blocked" },
];

export default function FriendsScreen() {
    const t = useT();
    const code = useSettings((s) => s.friendCode);
    const ensureCode = useSettings((s) => s.ensureFriendCode);
    const regen = useSettings((s) => s.regenerateFriendCode);

    useEffect(() => {
        ensureCode();
    }, [ensureCode]);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(code || "");
            toast.success("Copied");
        } catch (e) {
            toast.error("Copy failed");
        }
    };

    return (
        <LobbyLayout active={SCREEN.FRIENDS}>
            <div data-testid="friends-screen" className="w-full h-full overflow-hidden px-8 py-6 flex flex-col">
                <div className="mb-4">
                    <div className="axis-mono text-[10px] tracking-[0.5em] text-orange-300/80">
                        {t("friends.subtitle")}
                    </div>
                    <div className="axis-heading text-3xl tracking-[0.4em] text-white flex items-center gap-3">
                        <Users size={22} className="text-orange-400" />
                        {t("friends.title")}
                    </div>
                </div>

                {/* Unavailable banner */}
                <div className="axis-cut-sm border border-orange-400/40 bg-orange-500/10 px-4 py-2.5 mb-4 flex items-center gap-3">
                    <span className="axis-mono text-[10px] tracking-[0.4em] text-orange-300 px-2 py-0.5 border border-orange-400/60 axis-cut-sm">
                        BETA
                    </span>
                    <span className="axis-mono text-xs text-white/70">
                        {t("friends.unavailable")}
                    </span>
                </div>

                {/* My friend code */}
                <div className="axis-glass axis-cut-sm p-5 border border-white/10 mb-4">
                    <div className="axis-mono text-[10px] tracking-[0.4em] text-white/50 mb-2">
                        {t("friends.my_code")}
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                        <div
                            data-testid="friend-code"
                            className="axis-heading text-3xl tracking-[0.5em] text-white bg-black/40 axis-cut-sm px-6 py-3 border border-white/10"
                            style={{ fontFamily: "'JetBrains Mono', monospace" }}
                        >
                            {code || "------"}
                        </div>
                        <button
                            data-testid="friend-copy"
                            onClick={copy}
                            className="axis-btn axis-btn-ghost axis-cut-sm px-4 py-2 text-xs flex items-center gap-2"
                        >
                            <Copy size={13} /> {t("friends.copy_code")}
                        </button>
                        <button
                            data-testid="friend-reset"
                            onClick={() => { regen(); toast.success("New code generated"); }}
                            className="axis-btn axis-btn-ghost axis-cut-sm px-4 py-2 text-xs flex items-center gap-2"
                        >
                            <RefreshCw size={13} /> {t("friends.reset_code")}
                        </button>
                    </div>
                </div>

                {/* Tabs + panel */}
                <div className="flex gap-4 flex-1 overflow-hidden">
                    <div className="w-56 flex flex-col gap-1 overflow-y-auto">
                        {TABS.map((tab) => (
                            <div
                                key={tab.id}
                                className="axis-cut-sm px-3 py-2.5 border border-transparent text-white/50 axis-heading text-xs tracking-widest"
                            >
                                {t(tab.key)}
                                <span className="ml-2 axis-mono text-[9px] text-white/25">0</span>
                            </div>
                        ))}
                    </div>
                    <div className="flex-1 axis-cut-sm border border-white/10 bg-black/30 p-5 flex flex-col overflow-hidden">
                        <div className="flex items-center gap-2 mb-4">
                            <div className="flex-1 flex items-center gap-2 axis-cut-sm border border-white/10 px-3 py-2 bg-black/40">
                                <Search size={13} className="text-white/40" />
                                <input
                                    disabled
                                    placeholder={`${t("friends.search")}...`}
                                    className="bg-transparent flex-1 axis-mono text-xs text-white/60 outline-none disabled:cursor-not-allowed"
                                />
                            </div>
                            <button
                                disabled
                                className="axis-cut-sm px-4 py-2 border border-white/15 text-white/50 axis-mono text-[11px] tracking-widest flex items-center gap-2 disabled:cursor-not-allowed"
                            >
                                <UserPlus size={13} /> {t("friends.add")}
                            </button>
                        </div>
                        <div className="flex-1 flex flex-col items-center justify-center text-center">
                            <Ban size={30} className="text-white/25 mb-3" />
                            <div className="axis-mono text-[10px] tracking-[0.4em] text-white/40 max-w-md">
                                {t("friends.unavailable")}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </LobbyLayout>
    );
}
